import { todaySaoPaulo } from "../../utils/format";
import React, { useEffect, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, Building2, ClipboardList, Hospital, ShieldCheck, WandSparkles } from "lucide-react";

function SectionCard({ icon: Icon, eyebrow, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
      <div className="flex items-start gap-3 border-b border-border/70 bg-muted/20 px-4 py-4 sm:px-6">
        <div className="grid size-10 shrink-0 place-items-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
          <Icon className="size-5" />
        </div>
        <div>
          <div className="label-tech">{eyebrow}</div>
          <h2 className="mt-0.5 text-base font-semibold tracking-tight">{title}</h2>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </section>
  );
}

const lines = (value) => String(value || "").split(/\n|;/).map((x) => x.trim()).filter(Boolean);
const joined = (value) => (value || []).join("\n");

export default function PREForm() {
  const [clients, setClients] = useState([]);
  const [cenarios, setCenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    data_emissao: todaySaoPaulo(),
    responsavel_interno: "",
    caracteristicas_imovel: "",
    setores_existentes: [],
    processos_equipamentos: [],
    fontes_energia_presentes: [],
    recursos_emergencia_existentes: [],
    observacoes_operacionais: "",
    hospital_nome: "", hospital_telefone: "", hospital_endereco: "",
    cenarios_selecionados: [],
    riscos_especificos: [],
    cenarios_adicionais: [],
  });

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/cenarios").then((r) => setCenarios(r.data));
  }, []);

  const toggleCenario = (key) => setForm((f) => ({
    ...f,
    cenarios_selecionados: f.cenarios_selecionados.includes(key)
      ? f.cenarios_selecionados.filter((x) => x !== key)
      : [...f.cenarios_selecionados, key],
  }));

  const submit = async () => {
    if (!form.client_id) return toast.error("Selecione um cliente");
    setLoading(true);
    try {
      const r = await api.post("/documents/pre/generate", form);
      const docId = r.data.id;
      toast.success("Geração iniciada — acompanhe o progresso");
      const result = await pollStatus(docId);
      if (result.status === "failed") {
        toast.error(result.error || "Falha ao gerar PRE");
        return;
      }
      toast.success(`PRE gerado em ${result.elapsed_s}s via ${result.model_used}`);
      const blob = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(blob.data);
      const cli = (clients.find((c) => c.id === form.client_id)?.razao_social || "cliente").replace(/[\\/:*?"<>|]/g, "").trim();
      const a = document.createElement("a"); a.href = url; a.download = `PRE - ${cli || "cliente"}.docx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar");
    } finally { setLoading(false); }
  };

  const pollStatus = async (docId) => {
    const maxWaitMs = 5 * 60 * 1000;
    const intervalMs = 3000;
    const start = Date.now();
    while (true) {
      await new Promise((res) => setTimeout(res, intervalMs));
      const s = await api.get(`/documents/${docId}/status`);
      if (s.data.status === "completed") return s.data;
      if (s.data.status === "failed") return s.data;
      if (Date.now() - start > maxWaitMs) return { status: "failed", error: "Timeout aguardando geração (5min)" };
    }
  };

  return (
    <>
      <Header
        title="Plano de Resposta a Emergência (PRE)"
        subtitle="PRE baseado somente em fatos confirmados e cenários tecnicamente aplicáveis."
      />

      <div className="mx-auto w-full max-w-6xl space-y-5 p-4 sm:p-6 lg:p-8" data-testid="pre-form-page">
        <div className="flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm">
          <div className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="size-4" /></div>
          <div>
            <div className="font-semibold">Política anti-invenção ativa</div>
            <p className="mt-1 leading-relaxed text-muted-foreground">Atividade, CNAE e grau de risco não autorizam a IA a presumir setores, processos, equipamentos, gás, GLP, químicos, brigada ou recursos de emergência. O que não estiver confirmado abaixo deve ser omitido ou tratado de forma genérica.</p>
          </div>
        </div>

        <SectionCard icon={Building2} eyebrow="1 · Empresa" title="Cliente e emissão" description="O cadastro fornece somente os dados cadastrais e a atividade geral; ele não é usado para inventar a estrutura operacional.">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="label-tech">Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="pre-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-tech">Data de emissão</Label><Input type="date" value={form.data_emissao} onChange={(e) => setForm({ ...form, data_emissao: e.target.value })} /></div>
          </div>
        </SectionCard>

        <SectionCard icon={ClipboardList} eyebrow="2 · Realidade confirmada" title="Como esta unidade realmente funciona" description="Informe apenas o que você confirmou no cliente. Uma linha por item. Campo vazio significa: não afirmar no PRE.">
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label className="label-tech">Responsável interno</Label><Input value={form.responsavel_interno} onChange={(e) => setForm({ ...form, responsavel_interno: e.target.value })} placeholder="Se não houver, deixe vazio" /></div>
            <div><Label className="label-tech">Características confirmadas do imóvel</Label><Input value={form.caracteristicas_imovel} onChange={(e) => setForm({ ...form, caracteristicas_imovel: e.target.value })} placeholder="Ex.: galpão térreo de 800 m²" /></div>
            <div><Label className="label-tech">Setores / ambientes existentes</Label><Textarea rows={4} value={joined(form.setores_existentes)} onChange={(e) => setForm({ ...form, setores_existentes: lines(e.target.value) })} placeholder={"Ex.:\nRecepção\nProdução\nExpedição"} /></div>
            <div><Label className="label-tech">Processos / equipamentos existentes</Label><Textarea rows={4} value={joined(form.processos_equipamentos)} onChange={(e) => setForm({ ...form, processos_equipamentos: lines(e.target.value) })} placeholder={"Ex.:\nPrensa hidráulica\nMáquinas de costura\nEmpilhadeira"} /></div>
            <div><Label className="label-tech">Fontes de energia / combustíveis existentes</Label><Textarea rows={4} value={joined(form.fontes_energia_presentes)} onChange={(e) => setForm({ ...form, fontes_energia_presentes: lines(e.target.value) })} placeholder={"Ex.:\nEnergia elétrica\nGLP — somente se realmente houver"} /></div>
            <div><Label className="label-tech">Recursos de emergência existentes</Label><Textarea rows={4} value={joined(form.recursos_emergencia_existentes)} onChange={(e) => setForm({ ...form, recursos_emergencia_existentes: lines(e.target.value) })} placeholder={"Ex.:\nExtintores portáteis\nIluminação de emergência\nAlarme de incêndio"} /></div>
            <div className="md:col-span-2"><Label className="label-tech">Observações operacionais confirmadas</Label><Textarea rows={3} value={form.observacoes_operacionais} onChange={(e) => setForm({ ...form, observacoes_operacionais: e.target.value })} placeholder="Use para fatos relevantes que não se encaixam nos campos acima. Não coloque hipóteses." /></div>
          </div>
        </SectionCard>

        <SectionCard icon={AlertTriangle} eyebrow="3 · Cenários" title="Cenários de emergência aplicáveis" description="Selecione os cenários que realmente existem ou são tecnicamente aplicáveis à unidade.">
          <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3 text-xs leading-relaxed text-muted-foreground">
            Se nenhum cenário for selecionado, o PRE usará apenas um conjunto básico não presuntivo: incêndio, mal súbito, evacuação e falta de energia. O sistema <strong className="text-foreground">não incluirá gás, químicos, altura, espaço confinado, empilhadeira ou riscos geográficos automaticamente</strong> apenas pela atividade ou município.
          </div>
          <div className="mb-3 flex items-center justify-between gap-3"><Label className="label-tech">Catálogo</Label><span className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground">{form.cenarios_selecionados.length} de {cenarios.length} selecionados</span></div>
          <div className="grid max-h-[480px] grid-cols-1 gap-2 overflow-y-auto rounded-2xl border border-border/70 bg-muted/10 p-3 md:grid-cols-2">
            {cenarios.map((c) => {
              const checked = form.cenarios_selecionados.includes(c.key);
              return (
                <label key={c.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm transition-all ${checked ? "border-primary/30 bg-primary/10 shadow-sm" : "border-transparent bg-background/70 hover:border-border hover:bg-muted/30"}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleCenario(c.key)} className="mt-0.5" />
                  <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="font-medium leading-tight">{c.titulo}</span><Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[9px]">{c.etapas.length} etapas</Badge></div><div className="mt-1 line-clamp-1 text-xs text-muted-foreground">{c.localidade}</div></div>
                </label>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard icon={Hospital} eyebrow="4 · Apoio externo" title="Hospital de referência" description="Se não houver informação confirmada, deixe vazio; o documento registrará que deve ser definido pelo cliente.">
          <div className="grid gap-4 md:grid-cols-3">
            <div><Label className="label-tech">Hospital</Label><Input placeholder="Nome do hospital" value={form.hospital_nome} onChange={(e) => setForm({ ...form, hospital_nome: e.target.value })} /></div>
            <div><Label className="label-tech">Telefone</Label><Input placeholder="Telefone" value={form.hospital_telefone} onChange={(e) => setForm({ ...form, hospital_telefone: e.target.value })} /></div>
            <div><Label className="label-tech">Endereço</Label><Input placeholder="Endereço" value={form.hospital_endereco} onChange={(e) => setForm({ ...form, hospital_endereco: e.target.value })} /></div>
          </div>
        </SectionCard>

        <div className="sticky bottom-3 z-20 rounded-2xl border border-border/80 bg-background/95 p-3 shadow-lg backdrop-blur sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-muted-foreground">{loading ? "Geração em andamento. Mantenha esta aba aberta." : "A geração usa temperatura reduzida e aplica uma segunda validação determinística contra fatos operacionais não confirmados."}</div>
            <Button onClick={submit} disabled={loading} size="lg" className="w-full sm:w-auto"><Sparkles className="mr-2 size-4" /> {loading ? "Gerando documento…" : "Gerar PRE"}</Button>
          </div>
          {loading && <div className="mt-3"><div className="h-1.5 overflow-hidden rounded-full bg-muted/50"><div className="h-full w-1/3 bg-primary" style={{ animation: "slideIndeterminate 1.8s infinite" }} /></div><p className="mt-2 text-right text-xs text-muted-foreground">Claude em modo estritamente factual.</p></div>}
        </div>
      </div>
    </>
  );
}

